import { useCallback, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { CrudListPage } from '@core/components';
import { useCrudList } from '@core/hooks/useCrudList';
import {
  type Vestment,
  deleteVestment,
  getAllVestments,
  reorderVestments,
} from '../../../api/vestment';
import VestmentCategoryDialog, {
  type EditableVestmentCategory,
} from '../../components/VestmentCategoryDialog';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { getCategory } from '../../enum/vestmentType';
import { getVestmentTypeLabel } from '../../utils/vestment';

const CATEGORY_LABELS: Record<
  EditableVestmentCategory,
  { singular: string; plural: string }
> = {
  [VestmentCategory.Alb]: { singular: 'Alb', plural: 'Albs' },
  [VestmentCategory.Mitre]: { singular: 'Mitre', plural: 'Mitres' },
};

interface VestmentCategoryListProps {
  category: EditableVestmentCategory;
}

const VestmentCategoryList = ({ category }: VestmentCategoryListProps) => {
  const { refreshContext } = useAppContext();
  const { singular, plural } = CATEGORY_LABELS[category];
  const loadCategoryVestments = useCallback(
    async () =>
      (await getAllVestments()).filter(
        (vestment) =>
          !vestment.isHidden && getCategory(vestment.type) === category
      ),
    [category]
  );
  const reorderCategoryVestments = useCallback(
    (ids: number[]) => reorderVestments(category, ids),
    [category]
  );

  const {
    items: vestments,
    loading,
    dialogOpen,
    editingItem: editingVestment,
    deleteConfirm,
    load: loadVestments,
    openCreate,
    closeDialog,
    cancelDelete,
    confirmDelete,
    getRowId,
    contextMenuItems,
    rowReorder,
  } = useCrudList<Vestment>({
    loadItems: loadCategoryVestments,
    deleteItem: (vestment) => deleteVestment(vestment.id),
    reorderItems: reorderCategoryVestments,
    loadErrorMessage: `Failed to load ${plural.toLowerCase()}`,
    deleteSuccessMessage: `${singular} deleted`,
    deleteErrorMessage: `Failed to delete ${singular.toLowerCase()}`,
    reorderErrorMessage: `Failed to reorder ${plural.toLowerCase()}`,
    refreshContext,
  });

  const columns = useMemo<GridColDef<Vestment>[]>(() => {
    const name: GridColDef<Vestment> = {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
    };
    if (category !== VestmentCategory.Alb) {
      return [name];
    }
    return [
      name,
      {
        field: 'type',
        headerName: 'Type',
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => getVestmentTypeLabel(row.type),
      },
    ];
  }, [category]);

  return (
    <CrudListPage<Vestment>
      variant="section"
      title={plural}
      rows={vestments}
      columns={columns}
      loading={loading}
      getRowId={getRowId}
      contextMenuItems={contextMenuItems}
      rowReorder={rowReorder}
      addConfig={{
        label: `Add ${singular}`,
        onAdd: openCreate,
      }}
      addEditDialog={
        <VestmentCategoryDialog
          open={dialogOpen}
          onClose={closeDialog}
          category={category}
          vestment={editingVestment}
          onSuccess={() => void loadVestments()}
        />
      }
      deleteConfig={{
        title: `Delete ${singular.toLowerCase()}`,
        item: deleteConfirm,
        getContent: (vestment) =>
          `Are you sure you want to delete "${vestment.name}"?`,
        onConfirm: confirmDelete,
        onCancel: cancelDelete,
      }}
    />
  );
};

export default VestmentCategoryList;
